import React, { useCallback } from 'react';
import { Card, Segmented, DatePicker, Button, Space } from 'antd';
import { CalendarOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

export const DATE_PRESETS = [
  { label: 'All',     days: null },
  { label: 'Today',   days: 0    },
  { label: '7 days',  days: 7    },
  { label: '30 days', days: 30   },
  { label: '90 days', days: 90   },
];

/**
 * Resolves the active preset + customRange into { from, to } ISO strings (or nulls for "All").
 */
export const resolveRange = (preset, customRange) => {
  if (customRange) {
    return {
      from: customRange[0].startOf('day').toISOString(),
      to:   customRange[1].endOf('day').toISOString(),
    };
  }
  const p = DATE_PRESETS.find(p => p.label === preset);
  if (!p || p.days === null) return { from: null, to: null };  // All time
  const to   = dayjs().endOf('day');
  const from = p.days === 0 ? dayjs().startOf('day') : dayjs().subtract(p.days, 'day').startOf('day');
  return { from: from.toISOString(), to: to.toISOString() };
};

/**
 * Shared date-filter bar for admin pages.
 *
 * Props:
 *   preset        string          — current preset label ('All' | 'Today' | '7 days' | '30 days' | '90 days' | 'Custom')
 *   setPreset     fn(string)
 *   customRange   [dayjs, dayjs] | null
 *   setCustomRange fn([dayjs,dayjs]|null)
 *   onRefresh     fn()             — called when Refresh button is clicked
 *   loading       bool
 *   extra         ReactNode        — optional right-side slot (e.g. chart type toggle)
 */
const AdminDateFilter = ({
  preset,
  setPreset,
  customRange,
  setCustomRange,
  onRefresh,
  loading,
  extra,
}) => {
  const handlePresetChange = useCallback(v => {
    if (v !== 'Custom') {
      setCustomRange(null);
      setPreset(v);
    }
  }, [setPreset, setCustomRange]);

  const handleRangeChange = useCallback(v => {
    setCustomRange(v);
    if (v) setPreset('Custom');
    else   setPreset('All');
  }, [setPreset, setCustomRange]);

  return (
    <Card
      style={{ borderRadius: 12, marginBottom: 16 }}
      styles={{ body: { padding: '10px 16px' } }}
    >
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <CalendarOutlined style={{ color: 'var(--brand-primary)', fontSize: 15 }} />

        <Segmented
          value={customRange ? 'Custom' : preset}
          onChange={handlePresetChange}
          options={[...DATE_PRESETS.map(p => p.label), 'Custom']}
        />

        <RangePicker
          value={customRange}
          onChange={handleRangeChange}
          disabledDate={d => d && d > dayjs()}
          format="MMM D, YYYY"
          style={{ borderRadius: 8 }}
          placeholder={['Start date', 'End date']}
        />

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          {extra}
          <Button
            icon={<ReloadOutlined />}
            onClick={onRefresh}
            loading={loading}
            size="small"
            style={{ borderRadius: 8 }}
          >
            Refresh
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default AdminDateFilter;
